/**
 * AI page-layout generator dialog (#181).
 *
 * Opens from the draft pane of `PageDetailPage`. A prompt textarea + the
 * shared `BudgetBanner` + a submit button. On success the parent page's
 * detail query is refetched (via `useGenerateLayout`'s `onSuccess`) so
 * the editor reflects the new structure without a manual refresh.
 *
 * Error handling mirrors the AI Studio surface:
 *   - 429 `AI_RATE_LIMITED` / `AI_BUDGET_EXCEEDED` → error toast (the
 *     banner above the prompt also surfaces the budget state).
 *   - 422 `AI_INVALID_PLAN` → error toast with the planner's diagnostic
 *     message — uncommon with the mock, only meaningful in production.
 *   - Other errors → generic error toast.
 *
 * MUI only — no Tailwind, CSS modules, or raw CSS per
 * `standards/react-patterns.md`.
 *
 * `data-testid` attributes match the issue brief's enumeration so QA can
 * attach Playwright specs in a follow-up:
 *   - `page-detail-ai-dialog`
 *   - `page-detail-ai-prompt`
 *   - `page-detail-ai-generate-submit`
 *   - `page-detail-ai-cancel`
 */

import { useState } from "react";
import type { ReactElement } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
} from "@mui/material";
import { useGenerateLayout } from "../../hooks/useAiStudio";
import { useToast } from "../../components/ToastProvider";
import { BudgetBanner } from "./BudgetBanner";
import type { ApiClientError } from "../../lib/api";

/** Mirrors the server-side cap on `aiGenerateLayoutSchema.prompt`. */
const PROMPT_MAX_LEN = 8_000;

export type PageLayoutGeneratorDialogProps = {
  open: boolean;
  onClose: () => void;
  /** The Page id the targeted draft belongs to. */
  pageId: string;
  /** The DRAFT PageVersion id to PATCH. */
  versionId: string;
};

export function PageLayoutGeneratorDialog({
  open,
  onClose,
  pageId,
  versionId,
}: PageLayoutGeneratorDialogProps): ReactElement {
  const toast = useToast();
  const [prompt, setPrompt] = useState("");
  const generate = useGenerateLayout(pageId);

  const trimmedLen = prompt.trim().length;
  const canSubmit = trimmedLen > 0 && trimmedLen <= PROMPT_MAX_LEN && !generate.isPending;

  const handleClose = () => {
    if (generate.isPending) return;
    setPrompt("");
    onClose();
  };

  const handleSubmit = () => {
    if (!canSubmit) return;
    generate.mutate(
      { versionId, input: { prompt: prompt.trim() } },
      {
        onSuccess: (data) => {
          toast.showSuccess(
            `Generated ${data.blocks.length} block(s). Editor refreshed with the new layout.`,
          );
          setPrompt("");
          onClose();
        },
        onError: (err: ApiClientError) => {
          if (err.code === "AI_RATE_LIMITED") {
            toast.showError("Too many AI calls this minute. Please wait and try again.");
            return;
          }
          if (err.code === "AI_BUDGET_EXCEEDED") {
            toast.showError(
              "Monthly AI budget exceeded. Start an override window from the banner above.",
            );
            return;
          }
          if (err.code === "AI_INVALID_PLAN") {
            toast.showError(
              "AI returned a layout plan that failed validation. Try a more specific prompt.",
            );
            return;
          }
          if (err.code === "VERSION_NOT_DRAFT") {
            toast.showError("This page version is no longer a draft. Refresh and try again.");
            return;
          }
          toast.showError(err.message || "Could not generate page layout.");
        },
      },
    );
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      data-testid="page-detail-ai-dialog"
    >
      <DialogTitle>Generate page layout with AI</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <BudgetBanner />
          <DialogContentText>
            Describe the page you want and the AI will draft a starting layout for you. New
            ContentItems will be created and the draft&apos;s structure will be replaced.
          </DialogContentText>
          <Box>
            <TextField
              label="Prompt"
              multiline
              minRows={5}
              maxRows={12}
              fullWidth
              autoFocus
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder={
                "e.g. Homepage for a SaaS landing page: a hero with the product name, " +
                "a 3-feature highlight, and a contact form."
              }
              error={trimmedLen > PROMPT_MAX_LEN}
              helperText={
                trimmedLen > PROMPT_MAX_LEN
                  ? `Prompt is ${String(trimmedLen)} characters — please trim to ${String(PROMPT_MAX_LEN)}.`
                  : `${String(trimmedLen)} / ${String(PROMPT_MAX_LEN)}`
              }
              data-testid="page-detail-ai-prompt"
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          disabled={generate.isPending}
          data-testid="page-detail-ai-cancel"
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit}
          data-testid="page-detail-ai-generate-submit"
        >
          {generate.isPending ? "Generating…" : "Generate layout"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
